import {
  ExportFormat,
  ExportMode,
  ExportOption,
  ExportType,
} from '@/modules/exports'
import { getExportPeriodIso } from '@/modules/exports/components/export-form/export-period'
import { endOfWeek, format } from '@/utils/date-fns'
import { notEmptyArrayValidation } from '@/utils/forms'
import { useCompany, useConfirm, useOnErrorAuto, useUser } from '@/utils/hooks'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import type { SelectOption } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  endOfDay,
  endOfMonth,
  formatISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { addDays } from 'date-fns/esm'
import { useCallback, useMemo } from 'react'
import { Module } from '../companies'
import type {
  ActivitiesWithReceiptCoolumnNode,
  AllDynamicColumnsQuery,
  AllDynamicColumnsQueryVariables,
} from '../dynamic_tables'
import { ALL_DYNAMIC_COLUMNS_QUERY, DynamicTable } from '../dynamic_tables'
import {
  useActivityExportMutations,
  useActivityMutations,
} from './mutations.hooks'
import {
  DRIVER_ACTIVITY_DAY_REPORT_QUERY,
  GET_ALL_DRIVER_ACTIVITIES,
  GET_DRIVER_ACTIVITIES_AGGREGATED,
  GET_ISSUES,
  GET_SPECIFIC_CONDITIONS,
} from './queries'
import type { ActivitiesForm, TypeChart } from './types'
import { UserOrderBy } from './types'
import type {
  DriverActivityDayReportQuery,
  DriverActivityDayReportQueryVariables,
  GetAllDriverActivitiesAggregated,
  GetIssues,
  GetSpecificConditionsData,
  ParamChart,
} from './types.graphql'
import { parseNumberToTimestamp } from './util'
import { ALL_INFRINGEMENTS_QUERY } from '../infringements'

export function useActivityApprovedHandlers() {
  const translations = useTranslate({
    prompt: {
      title: 'activities.approve-title',
      day: ['activities.approve-day', { date: '' }],
      week: [
        'activities.approve-week',
        { week: '', dateStart: '', dateEnd: '' },
      ],
      month: [
        'activities.approve-month',
        { month: '', dateStart: '', dateEnd: '' },
      ],
    },
  })

  const confirm = useConfirm()
  const mutations = useActivityMutations()

  async function handleApproveDay(
    userId: string,
    approve: boolean,
    datetimeStart: Date
  ) {
    const { data } = await confirm(
      translations.prompt.day({ date: format(datetimeStart, 'dd.MM') }),
      translations.prompt.title
    )
    if (!data) return
    const datetimeEnd = endOfDay(datetimeStart)
    void mutations.toggleApproved({
      variables: {
        user: userId,
        datetimeStart: datetimeStart.toISOString(),
        datetimeEnd: datetimeEnd.toISOString(),
        approve,
      },
    })
  }

  async function handleApproveWeek(
    userId: string,
    approve: boolean,
    datetimeStart: Date,
    datetimeEnd: Date
  ) {
    const { data } = await confirm(
      translations.prompt.week({
        week: format(datetimeStart, 'w'),
        dateStart: format(datetimeStart, 'dd.MM'),
        dateEnd: format(datetimeEnd, 'dd.MM'),
      }),
      translations.prompt.title
    )
    if (!data) return

    void mutations.toggleApproved({
      variables: {
        user: userId,
        datetimeStart: datetimeStart.toISOString(),
        datetimeEnd: datetimeEnd.toISOString(),
        approve,
      },
    })
  }

  async function handleApproveMonth(
    userId: string,
    approve: boolean,
    datetimeStart: Date,
    datetimeEnd: Date
  ) {
    const { data } = await confirm(
      translations.prompt.month({
        month: format(endOfWeek(datetimeStart), 'MMMM'),
        dateStart: format(datetimeStart, 'dd.MM'),
        dateEnd: format(datetimeEnd, 'dd.MM'),
      }),
      translations.prompt.title
    )
    if (!data) return

    void mutations.toggleApproved({
      variables: {
        user: userId,
        datetimeStart: datetimeStart.toISOString(),
        datetimeEnd: datetimeEnd.toISOString(),
        approve,
      },
    })
  }

  return {
    handler: {
      day: handleApproveDay,
      week: handleApproveWeek,
      month: handleApproveMonth,
    },
  }
}

export function useActivityExportForm(onCompleted?: () => void) {
  const translations = useTranslate({
    hours: 'common.hours',
    diet: 'common.diet',
    tours: 'common.tours',

    error: 'error.generic-server-error',
    mailSuccess: 'exports.success-email-process',
    selectUsersError: 'activities.errors.select-multiple-users',
  })

  const me = useUser()
  const company = useCompany()

  const { exportActivities, loading: mailLoading } =
    useActivityExportMutations(onCompleted)

  const exportFormat = useMemo<SelectOption<ExportType>[]>(
    () => [
      {
        value: ExportType.ACTIVITIES,
        label: translations.hours,
      },
      {
        value: ExportType.DIET,
        label: translations.diet,
      },
      {
        value: ExportType.TOURS,
        label: translations.tours,
      },
    ],
    [translations.diet, translations.hours, translations.tours]
  )

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    updateForm,
  } = useForm<ActivitiesForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      exportType: ExportType.ACTIVITIES,
      exportOption: ExportOption.DOWNLOAD,
      exportFormat: ExportFormat.PDF,
      orderBy: UserOrderBy.FAMILY_NAME,
      useDateRange: false,
      weekly: false,
      user: null,
      users: [],
    },
    validators: {
      users: value =>
        notEmptyArrayValidation(value) ? translations.selectUsersError : null,
    },
  })

  const handler = useCallback(
    <K extends keyof ActivitiesForm>(key: K) => {
      return (value: ActivitiesForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const getDateInterval = useCallback(
    (startDate: Date, endDate?: Date): [string, string] => {
      if (form.useDateRange) {
        const dateEnd = endDate || new Date()
        return [formatISO(startDate), formatISO(dateEnd)]
      }
      return [
        formatISO(startOfMonth(startDate)),
        formatISO(endOfMonth(startDate)),
      ]
    },
    [form.useDateRange]
  )

  const handleExport = useCallback(
    async (values: ActivitiesForm) => {
      if (!values.user) return
      const date = form.useDateRange
        ? form.dateStart
        : startOfMonth(values.month)
      const [dateStart, dateEnd] = getDateInterval(date, form.dateEnd)

      void exportActivities({
        variables: {
          exportType: values.exportType,
          exportFormat: values.exportFormat,
          userMail: me.id,
          company: company.id,
          dateStart: dateStart,
          dateEnd: dateEnd,
          user: values.user,
          exportOption: values.exportOption,
          weekly: values.weekly,
          exportMode: ExportMode.DRIVER,
        },
      })
    },
    [
      company.id,
      exportActivities,
      form.dateEnd,
      form.dateStart,
      form.useDateRange,
      getDateInterval,
      me.id,
    ]
  )

  return {
    form,
    errors,
    loading: mailLoading,
    mailLoading,
    exportFormat,

    handler,
    updateForm,
    submit,
    handleExport,
  }
}

export function useMassActivityExportForm(onCompleted?: () => void) {
  const translations = useTranslate({
    hours: 'common.hours',
    hoursWorkDay: 'common.hours-work-day',
    diet: 'common.diet',
    tours: 'common.tours',
    summary: 'activities.work-summary',
    overtime: 'common.overtime',

    error: 'error.generic-server-error',
    mailSuccess: 'exports.success-email-process',

    selectUsersError: 'activities.errors.select-multiple-users',
    pauseExport: 'activities.pause-export',
    pauseAndAvailabilityExport: 'activities.pause-and-availability-export',
    workDayExport: 'activities.work-day-report',
    overtimeAndNightWorkExport: 'activities.overtime-and-night-work',
  })

  const me = useUser()
  const company = useCompany()

  const { exportActivitiesMass, loading: mailLoading } =
    useActivityExportMutations(onCompleted)

  const exportTypes = useMemo<SelectOption<ExportType>[]>(
    () => [
      {
        value: ExportType.MASS_ACTIVITIES,
        label: translations.hours,
      },
      {
        value: ExportType.MASS_ACTIVITIES_WORK_DAY,
        label: translations.hoursWorkDay,
      },
      {
        value: ExportType.MASS_DIET,
        label: translations.diet,
      },
      {
        value: ExportType.MASS_TOURS,
        label: translations.tours,
      },
      {
        value: ExportType.MASS_SUMMARY,
        label: translations.summary,
      },
      {
        value: ExportType.MASS_ACTIVITIES_OVERTIME,
        label: translations.overtime,
      },
      {
        value: ExportType.MASS_PAUSE,
        label: translations.pauseExport,
      },
      {
        value: ExportType.MASS_PAUSE_WITH_AVAILABILITY,
        label: translations.pauseAndAvailabilityExport,
      },
      {
        value: ExportType.WORK_DAY_REPORT,
        label: translations.workDayExport,
      },
      {
        value: ExportType.OVERTIME_AND_NIGHT_WORK,
        label: translations.overtimeAndNightWorkExport,
      },
    ],
    [
      translations.diet,
      translations.hours,
      translations.hoursWorkDay,
      translations.overtime,
      translations.overtimeAndNightWorkExport,
      translations.pauseAndAvailabilityExport,
      translations.pauseExport,
      translations.summary,
      translations.tours,
      translations.workDayExport,
    ]
  )

  const excelExportTypes = useMemo(
    () => [
      ExportType.MASS_ACTIVITIES,
      ExportType.MASS_DIET,
      ExportType.WORK_DAY_REPORT,
      ExportType.OVERTIME_AND_NIGHT_WORK,
    ],
    []
  )

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    formValid,
    updateForm,
  } = useForm<ActivitiesForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      exportType: ExportType.MASS_ACTIVITIES,
      exportOption: ExportOption.DOWNLOAD,
      exportFormat: ExportFormat.PDF,
      useDateRange: false,
      user: null,
      users: [],
      orderBy: UserOrderBy.FAMILY_NAME,
      includeAbsences: false,
      weekly: false,
    },
    validators: {
      users: value =>
        notEmptyArrayValidation(value) ? translations.selectUsersError : null,
    },
    config: {
      initAsInvalid: true,
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'Export-activites-mass-form',
        transformValues: {
          month: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          dateStart: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          dateEnd: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          exportType: value => value as ExportType,
          exportFormat: value => value as ExportFormat,
          exportOption: value => value as ExportOption,
          weekly: value => value as boolean,
        },
      },
    },
  })

  const handler = useCallback(
    <K extends keyof ActivitiesForm>(key: K) => {
      return (value: ActivitiesForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const getExportDateInterval = useCallback(
    (): [string, string] =>
      getExportPeriodIso({
        useDateRange: form.useDateRange,
        month: form.month,
        dateStart: form.dateStart,
        dateEnd: form.dateEnd,
      }),
    [form.dateEnd, form.dateStart, form.month, form.useDateRange]
  )

  const handleExport = useCallback(
    async (values: ActivitiesForm) => {
      const [dateStart, dateEnd] = getExportDateInterval()
      const orderBy = values.orderBy ? values.orderBy : 'last_name'
      const includeAbsences = values.includeAbsences ?? false

      await exportActivitiesMass({
        variables: {
          exportType: values.exportType,
          exportFormat: values.exportFormat,
          user: me.id,
          users: form.users,
          company: company.id,
          dateStart: dateStart,
          dateEnd: dateEnd,
          orderBy: orderBy,
          includeAbsences: includeAbsences,
          exportOption: values.exportOption,
          weekly: values.weekly,
          leaderGroups: [],
        },
      })
    },
    [company.id, exportActivitiesMass, form.users, getExportDateInterval, me.id]
  )

  return {
    form,
    errors,
    loading: mailLoading,
    exportTypes,
    excelExportTypes,
    formValid,
    handler,
    updateForm,
    submit,
    handleExport,
    getExportDateInterval,
  }
}

export const useDriverQueries = (
  userId: string,
  type: TypeChart,
  tempDate: Date,
  dateString: string
) => {
  const onErrorAuto = useOnErrorAuto()

  function getDateRange(variant: TypeChart) {
    switch (variant) {
      case 'week': {
        const weekStart = startOfWeek(tempDate, { weekStartsOn: 1 })
        const weekEnd = endOfWeek(tempDate)
        return {
          startDate: formatISO(weekStart),
          endDate: formatISO(weekEnd),
          dateForQuery: formatISO(weekStart),
        }
      }
      case 'month': {
        const monthStart = startOfMonth(tempDate)
        const monthEnd = endOfMonth(tempDate)
        return {
          startDate: formatISO(monthStart),
          endDate: formatISO(monthEnd),
          dateForQuery: formatISO(monthStart),
        }
      }
      default: {
        return {
          startDate: formatISO(tempDate),
          endDate: formatISO(addDays(tempDate, 1)),
          dateForQuery: formatISO(tempDate),
        }
      }
    }
  }

  function handleTimezoneOffset(data: any, refetch: any, field: string) {
    const timezoneData = data?.[field]?.timezoneData
    if (!timezoneData?.offset) return
    // This is pretty ugly, but untill we have a CompanyTimezone solution this'll refetch
    // the correct activities if the user's browser is in a different timezone than
    // the report's timezone. Additionally this is required to correct the date of
    // of the report if the user's browser is in a timezone that's -x UTC and report's +y UTC while x > y on
    // the day to not get the previous day.
    //TODO: clean this up when CompanyTimezone is implemented
    const offset = timezoneData.offset
    if (offset !== tempDate.getTimezoneOffset() / -60) {
      const newFetchDateString =
        dateString + `T00:00:00+` + parseNumberToTimestamp(offset)
      refetch({
        user: userId,
        date: newFetchDateString,
        dateEnd: getDateRange(type).endDate,
      })
    }
  }

  const commonOptions = {
    skip: !userId,
    variables: {
      userId,
      dateStart: getDateRange(type).startDate,
      dateEnd: getDateRange(type).endDate,
    },
    onError: onErrorAuto(),
  }

  const { data, refetch: refetchActivities } = useQuery(
    GET_ALL_DRIVER_ACTIVITIES,
    {
      ...commonOptions,
      fetchPolicy: 'network-only',
      onCompleted: data =>
        handleTimezoneOffset(
          data,
          refetchActivities,
          'driverActivityPeriodChart'
        ),
    }
  )

  const { data: issuesData } = useQuery<GetIssues, ParamChart>(
    GET_ISSUES,
    commonOptions
  )

  const { data: infringementsData } = useQuery(ALL_INFRINGEMENTS_QUERY, {
    skip: !userId,
    variables: {
      users: [userId],
      infringementTimestamp_Gte: getDateRange(type).startDate,
      infringementTimestamp_Lt: getDateRange(type).endDate,
    },
    onError: onErrorAuto(),
  })

  const { data: specificConditionsData } = useQuery<
    GetSpecificConditionsData,
    ParamChart
  >(GET_SPECIFIC_CONDITIONS, commonOptions)

  const { data: driverDataAggregated, refetch: refetchAggregated } = useQuery<
    GetAllDriverActivitiesAggregated,
    ParamChart
  >(GET_DRIVER_ACTIVITIES_AGGREGATED, {
    ...commonOptions,
    fetchPolicy: 'network-only',
    onCompleted: data =>
      handleTimezoneOffset(
        data,
        refetchAggregated,
        'driverActivityPeriodChart'
      ),
  })

  const {
    loading: activityLoading,
    data: activityData,
    refetch: refetchDay,
  } = useQuery<
    DriverActivityDayReportQuery,
    DriverActivityDayReportQueryVariables
  >(DRIVER_ACTIVITY_DAY_REPORT_QUERY, {
    fetchPolicy: 'network-only',
    variables: { user: userId, date: formatISO(tempDate) },
    onError: onErrorAuto(),
    onCompleted: data =>
      handleTimezoneOffset(data, refetchDay, 'driverActivityDayReport'),
  })

  const { data: columnData } = useQuery<
    AllDynamicColumnsQuery<ActivitiesWithReceiptCoolumnNode>,
    AllDynamicColumnsQueryVariables
  >(ALL_DYNAMIC_COLUMNS_QUERY, {
    variables: {
      moduleName: Module.ACTIVITIES,
      table: DynamicTable.ACTIVITIES,
    },
    onError: onErrorAuto(),
  })

  return {
    activityLoading,
    activityData,
    driverDataAggregated,
    issuesData,
    infringementsData,
    specificConditionsData,
    data,
    columnData,
  }
}
